import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SIDEBAR_INFO_BANNER_STORAGE_KEY,
  TREE_GROUP_BY_FARM,
} from 'constants/uiConstants';
import type { TreeGroupBy, TreeYearFilter } from 'utils/FarmUtils';

export interface FarmState {
  selectedFarmId: string | null;
  selectedPaddockId: string | null;
  /** Key of the visually selected tree node (e.g. "org-xxx", "farm-xxx"). */
  activeTreeNodeKey: string | null;
  /** Calendar year the tree is filtered to, or TREE_YEAR_ALL for no filter. */
  treeYearFilter: TreeYearFilter;
  /** Which hierarchy the tree groups plans into. */
  treeGroupBy: TreeGroupBy;
  /** Whether the left sidebar (tree panel) is collapsed. */
  sidebarCollapsed: boolean;
  /** Whether the sidebar usage-hint banner is visible. Persisted to localStorage. */
  sidebarInfoBannerVisible: boolean;
  /**
   * Incremented by resetSidebarWidth. FarmsPage watches this value and
   * restores the sidebar to its default width whenever it changes.
   */
  sidebarWidthResetCounter: number;
}

const initialState: FarmState = {
  selectedFarmId: null,
  selectedPaddockId: null,
  activeTreeNodeKey: null,
  // Evaluated once at module load. farmReducer is not persisted, so every
  // reload starts on the current year, which is the required default.
  treeYearFilter: new Date().getFullYear(),
  treeGroupBy: TREE_GROUP_BY_FARM,
  sidebarCollapsed: false,
  sidebarWidthResetCounter: 0,
  sidebarInfoBannerVisible:
    localStorage.getItem(SIDEBAR_INFO_BANNER_STORAGE_KEY) !== 'false',
};

const farmSlice = createSlice({
  name: 'farm',
  initialState,
  reducers: {
    setSelectedFarmId: (state, action: PayloadAction<string | null>) => {
      state.selectedFarmId = action.payload;
    },
    setSelectedPaddockId: (state, action: PayloadAction<string | null>) => {
      state.selectedPaddockId = action.payload;
    },
    setActiveTreeNodeKey: (state, action: PayloadAction<string | null>) => {
      state.activeTreeNodeKey = action.payload;
    },
    setTreeYearFilter: (state, action: PayloadAction<TreeYearFilter>) => {
      state.treeYearFilter = action.payload;
    },
    setTreeGroupBy: (state, action: PayloadAction<TreeGroupBy>) => {
      state.treeGroupBy = action.payload;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    setSidebarInfoBannerVisible: (state, action: PayloadAction<boolean>) => {
      state.sidebarInfoBannerVisible = action.payload;
    },
    resetSidebarWidth: (state) => {
      state.sidebarWidthResetCounter += 1;
    },
    clearFarmSelection: (state) => {
      state.selectedFarmId = null;
      state.selectedPaddockId = null;
      state.activeTreeNodeKey = null;
      // sidebarCollapsed and sidebarInfoBannerVisible are persistent UI
      // preferences — they survive navigation and org switches. treeYearFilter
      // and treeGroupBy are treated the same way: they describe how the user
      // wants to look at the tree, not which node is selected in it.
    },
  },
});

export const {
  setSelectedFarmId,
  setSelectedPaddockId,
  setActiveTreeNodeKey,
  setTreeYearFilter,
  setTreeGroupBy,
  setSidebarCollapsed,
  setSidebarInfoBannerVisible,
  resetSidebarWidth,
  clearFarmSelection,
} = farmSlice.actions;

export default farmSlice.reducer;
